import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, ref: localStorage.getItem("hh_ref") || undefined });
      localStorage.removeItem("hh_ref");
      setUser(data.user);
      toast.success("Account created!");
      navigate("/campus");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Learn and teach real skills in minutes.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Name</Label>
          <Input data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Email</Label>
          <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Password</Label>
          <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
        </div>
        <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold h-11 rounded-full">
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>
      <p className="text-center text-sm text-slate-400">
        Already have an account?{" "}
        <Link to="/login" data-testid="link-login" className="text-amber-400 hover:text-amber-300 font-medium">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
